import { isPlatformServer } from '@angular/common'
import {
  Component, ChangeDetectionStrategy, Input, AfterViewInit, ElementRef, ViewChild, PLATFORM_ID, OnDestroy, DestroyRef, inject, DOCUMENT,
} from '@angular/core'
import {
  debounceTime, fromEvent,
} from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'kb-marquee',
  templateUrl: './marquee.component.html',
  styleUrls: ['./marquee.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class MarqueeComponent implements AfterViewInit, OnDestroy {
  @Input() public speed = 1
  @Input() public fill = true

  @ViewChild('contentElement', { static: true }) private contentElement!: ElementRef<HTMLDivElement>
  @ViewChild('pageElement', { static: true }) private pageElement!: ElementRef<HTMLDivElement>
  @ViewChild('wrapperElement', { static: true }) private wrapperElement!: ElementRef<HTMLDivElement>

  private pageWidth!: number
  private animationSpeedMultiplier!: number
  private clonedElements: HTMLElement[] = []
  private resizeObserver?: ResizeObserver

  private destroyRef = inject(DestroyRef)
  private hostElement = inject(ElementRef)
  private document = inject(DOCUMENT)
  private platformId = inject(PLATFORM_ID)

  public ngAfterViewInit(): void {
    this.browserSetup()
  }

  public ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.contentElement.nativeElement)
    }
  }

  private onContentResize(): void {
    for (const clonedElement of this.clonedElements) {
      clonedElement.remove()
    }
    this.clonedElements = []

    const hostWidth = this.hostElement.nativeElement.clientWidth
    if (this.fill) {
      const contentWidth = this.contentElement.nativeElement.clientWidth

      if (contentWidth > 0) {
        const contentCopies = Math.ceil(hostWidth / contentWidth) - 1
        for (let i = 0; i < contentCopies; i++) {
          // Fill the page with as many copies as needed to fill the whole width of the host
          const clonedContentElement = this.contentElement.nativeElement.cloneNode(true) as HTMLDivElement
          clonedContentElement.setAttribute('aria-hidden', 'true')
          this.clonedElements.push(clonedContentElement)
          this.pageElement.nativeElement.appendChild(clonedContentElement)
        }
      }
    }

    this.pageWidth = this.pageElement.nativeElement.clientWidth

    // Duplicate the page
    const clonedPageElement = this.pageElement.nativeElement.cloneNode(true) as HTMLDivElement
    clonedPageElement.setAttribute('aria-hidden', 'true')
    this.clonedElements.push(clonedPageElement)
    this.wrapperElement.nativeElement.appendChild(clonedPageElement)

    this.animationSpeedMultiplier = (this.pageWidth / 100) / this.speed

    this.wrapperElement.nativeElement.style.animationDuration = `${this.animationSpeedMultiplier}s`
    this.wrapperElement.nativeElement.style.animationDelay = `${-this.animationSpeedMultiplier}s`
  }

  private browserSetup(): void {
    if (isPlatformServer(this.platformId)) {
      return
    }

    this.onContentResize()

    this.resizeObserver = new ResizeObserver(this.onContentResize.bind(this))
    this.resizeObserver.observe(this.contentElement.nativeElement)

    fromEvent(window, 'resize').pipe(
      debounceTime(1000),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.onContentResize()
    })
  }

  private startMouseX!: number
  private startAnimationDelay!: number
  private boundPointerMove = this.onPointerMove.bind(this)

  protected onPointerDown(event: PointerEvent): void {
    this.startMouseX = event.clientX
    this.startAnimationDelay = parseFloat(this.wrapperElement.nativeElement.style.animationDelay) + this.animationSpeedMultiplier

    this.document.addEventListener('pointermove', this.boundPointerMove)
    this.document.addEventListener('pointerup', () => {
      this.document.removeEventListener('pointermove', this.boundPointerMove)
    }, { once: true })
  }

  private onPointerMove(event: PointerEvent): void {
    const percentLeft = ((event.clientX - this.startMouseX) / this.pageWidth)
    const newAnimationDelay = (percentLeft * this.animationSpeedMultiplier) - this.animationSpeedMultiplier + this.startAnimationDelay
    if (newAnimationDelay > 0) {
      this.startAnimationDelay -= this.animationSpeedMultiplier
    }
    this.wrapperElement.nativeElement.style.animationDelay = `${newAnimationDelay}s`
  }
}
